import {
  type OnFarmImplantEmbryoPick,
  type OnFarmImplantShipmentContents,
  type OnFarmImplantShipmentSourceType,
  type OnFarmImplantShipmentSummary,
  toRecordKey,
} from '@/services/on-farm-implant';

export type PickedShipment = {
  shipmentId: string;
  shipmentNumber: string;
  sourceType: OnFarmImplantShipmentSourceType;
  recordKeys: string[];
  addedRecordKeys: string[];
  embryoKeys: string[];
  totalRecords: number;
  totalEmbryos: number;
};

const unique = (keys: string[]) => [...new Set(keys)];

export const toEmbryoPickKey = (
  pick: Pick<OnFarmImplantEmbryoPick, 'embryoId' | 'accountId'>,
) => toRecordKey(pick.embryoId, pick.accountId);

export const toEmbryoPickMatingKey = (
  pick: Pick<OnFarmImplantEmbryoPick, 'matingId' | 'accountId'>,
) => toRecordKey(pick.matingId, pick.accountId);

export const embryoPickMatingKeys = (picks: OnFarmImplantEmbryoPick[]) =>
  unique(picks.map(toEmbryoPickMatingKey));

export const isSameShipment = (
  a: Pick<PickedShipment, 'shipmentId' | 'sourceType'>,
  b: Pick<PickedShipment, 'shipmentId' | 'sourceType'>,
) =>
  a.sourceType === b.sourceType &&
  a.shipmentId.toLowerCase() === b.shipmentId.toLowerCase();

export const toShipmentEmbryoPicks = (
  contents: OnFarmImplantShipmentContents,
): OnFarmImplantEmbryoPick[] =>
  contents.embryos.map((embryo) => ({
    ...embryo,
    shipmentId: embryo.shipmentId ?? contents.shipmentId,
    shipmentNumber: embryo.shipmentNumber ?? contents.shipmentNumber,
  }));

export const toPickedShipment = (
  summary: OnFarmImplantShipmentSummary,
  contents: OnFarmImplantShipmentContents,
): PickedShipment => {
  if (contents.sourceType === 'frozen') {
    return {
      shipmentId: contents.shipmentId,
      shipmentNumber: contents.shipmentNumber,
      sourceType: 'frozen',
      recordKeys: [],
      addedRecordKeys: [],
      embryoKeys: unique(contents.embryos.map(toEmbryoPickKey)),
      totalRecords: embryoPickMatingKeys(contents.embryos).length,
      totalEmbryos: unique(contents.embryos.map(toEmbryoPickKey)).length,
    };
  }
  return {
    shipmentId: contents.shipmentId,
    shipmentNumber: contents.shipmentNumber,
    sourceType: 'fresh',
    recordKeys: unique(
      contents.records.map((record) =>
        toRecordKey(record.recordId, record.accountId),
      ),
    ),
    addedRecordKeys: [],
    embryoKeys: [],
    totalRecords: summary.totalRecords,
    totalEmbryos: summary.totalEmbryos,
  };
};

export const addFreshShipment = (
  currentKeys: string[],
  shipment: PickedShipment,
  existing?: PickedShipment,
): { recordKeys: string[]; shipment: PickedShipment; added: string[] } => {
  const current = new Set(currentKeys);
  const added = shipment.recordKeys.filter((key) => !current.has(key));
  return {
    recordKeys: [...currentKeys, ...added],
    shipment: {
      ...shipment,
      addedRecordKeys: unique([...(existing?.addedRecordKeys ?? []), ...added]),
    },
    added,
  };
};

export const removeFreshShipment = (
  currentKeys: string[],
  removed: PickedShipment,
  remaining: PickedShipment[],
): { recordKeys: string[]; shipments: PickedShipment[] } => {
  const stillCovered = new Set(
    remaining
      .filter((shipment) => shipment.sourceType === 'fresh')
      .flatMap((shipment) => shipment.recordKeys),
  );
  const dropped = new Set(
    removed.addedRecordKeys.filter((key) => !stillCovered.has(key)),
  );
  const handedOver = removed.addedRecordKeys.filter((key) =>
    stillCovered.has(key),
  );
  const shipments = remaining.map((shipment) => {
    if (shipment.sourceType !== 'fresh' || handedOver.length === 0) {
      return shipment;
    }
    const contents = new Set(shipment.recordKeys);
    const inherited = handedOver.filter((key) => contents.has(key));
    if (inherited.length === 0) return shipment;
    return {
      ...shipment,
      addedRecordKeys: unique([...shipment.addedRecordKeys, ...inherited]),
    };
  });
  return {
    recordKeys: currentKeys.filter((key) => !dropped.has(key)),
    shipments,
  };
};

export const addShipmentEmbryoPicks = (
  currentPicks: OnFarmImplantEmbryoPick[],
  newPicks: OnFarmImplantEmbryoPick[],
): OnFarmImplantEmbryoPick[] => {
  const seen = new Set(currentPicks.map(toEmbryoPickKey));
  const added: OnFarmImplantEmbryoPick[] = [];
  newPicks.forEach((pick) => {
    const key = toEmbryoPickKey(pick);
    if (seen.has(key)) return;
    seen.add(key);
    added.push(pick);
  });
  return added.length === 0 ? currentPicks : [...currentPicks, ...added];
};

export const removeShipmentEmbryoPicks = (
  currentPicks: OnFarmImplantEmbryoPick[],
  removed: PickedShipment,
  remaining: PickedShipment[],
): OnFarmImplantEmbryoPick[] => {
  const stillCovered = new Set(
    remaining
      .filter((shipment) => shipment.sourceType === 'frozen')
      .flatMap((shipment) => shipment.embryoKeys),
  );
  const dropped = new Set(
    removed.embryoKeys.filter((key) => !stillCovered.has(key)),
  );
  return currentPicks.filter((pick) => !dropped.has(toEmbryoPickKey(pick)));
};

export const buildFrozenShipmentsFromPicks = (
  picks: OnFarmImplantEmbryoPick[],
): PickedShipment[] => {
  const groups = new Map<string, OnFarmImplantEmbryoPick[]>();
  picks.forEach((pick) => {
    if (!pick.shipmentId) return;
    const groupKey = pick.shipmentId.toLowerCase();
    groups.set(groupKey, [...(groups.get(groupKey) ?? []), pick]);
  });
  return [...groups.values()].map((group) => {
    const embryoKeys = unique(group.map(toEmbryoPickKey));
    return {
      shipmentId: group[0].shipmentId ?? '',
      shipmentNumber: group[0].shipmentNumber ?? group[0].shipmentId ?? '',
      sourceType: 'frozen',
      recordKeys: [],
      addedRecordKeys: [],
      embryoKeys,
      totalRecords: embryoPickMatingKeys(group).length,
      totalEmbryos: embryoKeys.length,
    };
  });
};

export type ShipmentCoverage = { selected: number; total: number };

export const shipmentCoverage = (
  contents: OnFarmImplantShipmentContents,
  selectedRecordKeys: ReadonlySet<string>,
  embryoPickKeys: ReadonlySet<string>,
): ShipmentCoverage => {
  if (contents.sourceType === 'frozen') {
    const matingByEmbryo = new Map(
      contents.embryos.map((embryo) => [
        toEmbryoPickKey(embryo),
        toEmbryoPickMatingKey(embryo),
      ]),
    );
    let selected = 0;
    matingByEmbryo.forEach((matingKey, embryoKey) => {
      if (embryoPickKeys.has(embryoKey) || selectedRecordKeys.has(matingKey)) {
        selected += 1;
      }
    });
    return { selected, total: matingByEmbryo.size };
  }
  const keys = unique(
    contents.records.map((record) =>
      toRecordKey(record.recordId, record.accountId),
    ),
  );
  return {
    selected: keys.filter((key) => selectedRecordKeys.has(key)).length,
    total: keys.length,
  };
};

export type ShipmentInSelection = PickedShipment & { selectedCount: number };

export const freshShipmentsInSelection = (
  shipments: PickedShipment[],
  selectedRecordKeys: ReadonlySet<string>,
): ShipmentInSelection[] =>
  shipments
    .filter((shipment) => shipment.sourceType === 'fresh')
    .map((shipment) => ({
      ...shipment,
      selectedCount: shipment.recordKeys.filter((key) =>
        selectedRecordKeys.has(key),
      ).length,
    }))
    .filter((shipment) => shipment.selectedCount > 0);

const freshShipmentsStorageKey = (sessionId: string) =>
  `on-farm-implant-fresh-shipments:${sessionId}`;

export const readStoredFreshShipments = (
  sessionId: string | null | undefined,
): PickedShipment[] => {
  if (!sessionId) return [];
  try {
    const raw = sessionStorage.getItem(freshShipmentsStorageKey(sessionId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return (parsed as PickedShipment[]).filter(
      (shipment) =>
        shipment?.sourceType === 'fresh' &&
        Array.isArray(shipment.recordKeys) &&
        Array.isArray(shipment.addedRecordKeys),
    );
  } catch {
    return [];
  }
};

export const writeStoredFreshShipments = (
  sessionId: string | null | undefined,
  shipments: PickedShipment[],
) => {
  if (!sessionId) return;
  try {
    const key = freshShipmentsStorageKey(sessionId);
    if (shipments.length > 0) {
      sessionStorage.setItem(key, JSON.stringify(shipments));
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    return;
  }
};

export type ShipmentAddResult = {
  shipment: PickedShipment;
  previous?: PickedShipment;
  addedKeys: string[];
  rowKeys: string[];
  alreadySelected: number;
};
